"use client";
import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import { Messaging, onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import { getFirebaseApp, getFirebaseMessaging } from "@/app/lib/firebaseClient";
import { useRouter } from "next/navigation";

export const NotificationsContext = createContext<Messaging | undefined>(
  undefined
);

export const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [messaging, setMessaging] = useState<Messaging | undefined>(undefined);
  const router = useRouter();

  useEffect(() => {
    try {
      if ("serviceWorker" in navigator && "Notification" in window) {
        // Use the centralized Firebase client
        const messagingInstance = getFirebaseMessaging();
        setMessaging(messagingInstance);

        const unsubscribe = onMessage(messagingInstance, async (payload) => {
          if (Notification.permission === "granted" && payload.notification) {
            const url = payload.data?.url || "/";
            const notificationType = payload.data?.notificationType;
            const employeeId = payload.data?.employeeId;
            const employeeName = payload.data?.employeeName;
            
            const registration = await navigator.serviceWorker.ready;
            const notification = await registration.showNotification(
              payload?.notification?.title || "",
              {
                body: payload.notification?.body,
                icon: "/images/logo/black_android-chrome-512x512.png",
                data: { 
                  url,
                  notificationType,
                  employeeId,
                  employeeName
                },
                tag: notificationType || 'default', // Group notifications by type
                requireInteraction: false,
              }
            );
            
            // Add click handler for foreground notifications
            // Listen for notification click events
            navigator.serviceWorker.addEventListener('message', (event) => {
              if (event.data?.type === 'NOTIFICATION_CLICKED') {
                const clickedUrl = event.data.url;
                if (clickedUrl) {
                  console.log('[Foreground] Navigating to:', clickedUrl);
                  router.push(clickedUrl);
                }
              }
            });
          }
        });

        return () => unsubscribe();
      }
      else {
        console.log("Not supported");
      }
    }
    catch (error) {
      toast.error(`Error initializing Firebase: ${error}`);
      console.error("Error initializing Firebase:", error);
    }
  }, [router]);

  return (
    <NotificationsContext.Provider value={messaging}>
      {children}
    </NotificationsContext.Provider>
  );
};